import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import * as Yup from "yup";

import FormBuilder from "../../../../shared/components/form-builder/form-builder";
import { ATBuyerSellerDetails } from "../../atc-models/atc-org";
import useNotification from "../../../../shared/hooks/useNotification";
import { buyerSellerDetailsService } from "../../../v4/transaction-certificates/tc-service/buyer-seller-details.service";
import { useRecentTEIds } from "../../../../shared/hooks/useRecentTEIds";

// ------------------ Props ------------------
interface BuyerSellerDetailsFormProps {
  formRef: React.RefObject<any>;
  detailsData: ATBuyerSellerDetails;
  setDetailsData: (data: ATBuyerSellerDetails) => void;
}

// ------------------ Component ------------------
const BuyerSellerDetailsForm: React.FC<BuyerSellerDetailsFormProps> = ({
  formRef,
  detailsData,
  setDetailsData,
}) => {
  // const formRef = useRef<HTMLFormElement>(null);
  const { notify } = useNotification();
  const { recentTEIds, saveRecentlySelectedTEId } = useRecentTEIds();

  // ------------------ State ------------------
  const [visible, setVisible] = useState({
    sellerName: true,
    buyerName: true,
    sellerTeIdLabel: true, 
    buyerTeIdLabel: true,
  });

  const [disable, setDisable] = useState({
    sellerAddress: true,
    sellerState: true,
    sellerCountry: true,
    sellerPostalCode: true,
    buyerAddress: true,
    buyerState: true,
    buyerCountry: true,
    buyerPostalCode: true,
    buyerScNumber: true,
  });

  const [sellerHasTeid, setSellerHasTeid] = useState<"Yes" | "No">("Yes");

  // ------------------ Validation ------------------
const validationSchema = Yup.object().shape({
  sellerTeId: Yup.string(), // may be text or search based on flag
  sellerAddress: Yup.string().required("Seller Address is required"),
  sellerName: Yup.string().required("Seller Name is required"),
  sellerState: Yup.string().required("Seller State is required"),
  sellerCountry: Yup.string().required("Seller Country is required"), 
  sellerPostalCode: Yup.string(), 
  buyerTeId: Yup.string().required("Buyer TE ID is required"),
  buyerAddress: Yup.string().required("Buyer Address is required"),
  buyerName: Yup.string().required("Buyer Name is required"),
  buyerScNumber: Yup.string().required("Buyer SC Number is required"),
  buyerState: Yup.string().required("Buyer State is required"), 
  buyerCountry: Yup.string().required("Buyer Country is required"), 
  buyerPostalCode: Yup.string(), 
});

  // ------------------ API ------------------
  const fetchTEDetailsFromAPI = async (
    value: string,
    offset: number,
    type: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      return response.data || [];
    } catch (error) {
      console.error("Error fetching TE details:", error);
      notify("Failed to fetch TE details", "error");
      return [];
    }
  };

  // ------------------ Seller Handlers ------------------
  const handleSellerTEIdValueChange = (value: any) => {
    if (value == null) {
      // Reset
      [
        "sellerTeId",
        "sellerName",
        "sellerAddress",
        "sellerState",
        "sellerCountry",
        "sellerPostalCode",
      ].forEach((field) => formRef.current?.setFieldValue(field, ""));

      setDisable((prev) => ({
        ...prev,
        sellerName: false,
        sellerAddress: false,
        sellerState: false,
        sellerCountry: false,
        sellerPostalCode: false,
      }));

      setVisible((prev) => ({
        ...prev,
        sellerName: true,
        sellerTeIdLabel: true,
      }));
    } else {
      // Populate
      // Save selected TE details to recent list
      saveRecentlySelectedTEId(value);
      formRef.current?.setFieldValue("sellerTeId", value.TE_ID);
      formRef.current?.setFieldValue("sellerName", value.account_name);
      formRef.current?.setFieldValue(
        "sellerAddress",
        value.street || ""
      );
      formRef.current?.setFieldValue(
        "sellerState",
        value.state_name || ""
      );
      formRef.current?.setFieldValue(
        "sellerCountry",
        value.country_name || ""
      );
      formRef.current?.setFieldValue(
        "sellerPostalCode",
        value.postal_code || ""
      );
      
      setDisable((prev) => ({
        ...prev,
        sellerName: Boolean(value.account_name),
        sellerAddress: Boolean(value.street),
        sellerState: Boolean(value.state_name),
        sellerCountry: Boolean(value.country_name),
        sellerPostalCode: Boolean(value.postal_code),
      }));

      setVisible((prev) => ({ ...prev, sellerName: false }));
    }
  };

  const handleSellerNamedValueChange = (value: any) => {
    if (value == null) {
      [
        "sellerAddress",
        "sellerName",
        "sellerTeId",
        "sellerState",
        "sellerCountry",
        "sellerPostalCode",
      ].forEach((field) => formRef.current?.setFieldValue(field, ""));

      setDisable((prev) => ({
        ...prev,
        sellerAddress: false,
        sellerState: false,
        sellerCountry: false,
        sellerPostalCode: false,
      }));
      setVisible((prev) => ({ ...prev, sellerTeIdLabel: true }));
    } else {
      formRef.current?.setFieldValue("sellerName", value.account_name);
      formRef.current?.setFieldValue("sellerTeId", value.TE_ID);

      // Address
      if (value.street) {
        formRef.current?.setFieldValue("sellerAddress", value.street);
        setDisable((prev) => ({ ...prev, sellerAddress: true }));
      } else {
        formRef.current?.setFieldValue("sellerAddress", "");
        setDisable((prev) => ({ ...prev, sellerAddress: false }));
      }

      // State
      if (value.state_name) {
        formRef.current?.setFieldValue("sellerState", value.state_name);
        setDisable((prev) => ({ ...prev, sellerState: true }));
      } else {
        formRef.current?.setFieldValue("sellerState", "");
        setDisable((prev) => ({ ...prev, sellerState: false }));
      }

      // Country
      if (value.country_name) {
        formRef.current?.setFieldValue("sellerCountry", value.country_name);
        setDisable((prev) => ({ ...prev, sellerCountry: true }));
      } else {
        formRef.current?.setFieldValue("sellerCountry", "");
        setDisable((prev) => ({ ...prev, sellerCountry: false }));
      }

      // Postal Code
      if (value.postal_code) {
        formRef.current?.setFieldValue("sellerPostalCode", value.postal_code);
        setDisable((prev) => ({ ...prev, sellerPostalCode: true }));
      } else {
        formRef.current?.setFieldValue("sellerPostalCode", "");
        setDisable((prev) => ({ ...prev, sellerPostalCode: false }));
      }

      // // Licensing Code
      // if (value.cb_licensing_code) {
      //   formRef.current?.setFieldValue(
      //     "sellerCbLicensingCode",
      //     value.cb_licensing_code
      //   );
      //   setDisable((prev) => ({ ...prev, sellerCbLicensingCode: true }));
      // } else {
      //   formRef.current?.setFieldValue("sellerCbLicensingCode", "");
      //   setDisable((prev) => ({ ...prev, sellerCbLicensingCode: false }));
      // }

      setVisible((prev) => ({ ...prev, sellerTeIdLabel: false }));
    }
  };

  // ------------------ Buyer Handlers ------------------
  const handleBuyerTEIdValueChange = (value: any) => {
    if (value == null) {
      ["buyerTeId", "buyerName", "buyerAddress", "buyerScNumber", "buyerState", "buyerCountry", "buyerPostalCode"].forEach(
        (field) => formRef.current?.setFieldValue(field, "")
      );

      setDisable((prev) => ({
        ...prev,
        buyerName: false,
        buyerAddress: false,
        buyerScNumber: false,
        buyerState: false,
        buyerCountry: false,
        buyerPostalCode: false,
      }));
      setVisible((prev) => ({
        ...prev,
        buyerName: true,
        buyerTeIdLabel: true,
      }));
    } else {
      // Save selected TE details to recent list
      saveRecentlySelectedTEId(value);
      formRef.current?.setFieldValue("buyerTeId", value.TE_ID);
      formRef.current?.setFieldValue("buyerName", value.account_name);
      formRef.current?.setFieldValue(
        "buyerAddress",
        value.street || ""
      );
      formRef.current?.setFieldValue(
        "buyerScNumber",
        value.sc_number || ""
      );
      formRef.current?.setFieldValue(
        "buyerState",
        value.state_name || ""
      );
      formRef.current?.setFieldValue(
        "buyerCountry",
        value.country_name || ""
      );
      formRef.current?.setFieldValue(
        "buyerPostalCode",
        value.postal_code || ""
      );

      setDisable((prev) => ({
        ...prev,
        buyerName: Boolean(value.account_name),
        buyerAddress: Boolean(value.street),
        buyerScNumber: false, // always editable
        buyerState: Boolean(value.state_name),
        buyerCountry: Boolean(value.country_name),
        buyerPostalCode: Boolean(value.postal_code),
      }));

      setVisible((prev) => ({ ...prev, buyerName: false }));
    }
  };

  const handleBuyerNameValueChange = (value: any) => {
    if (value == null) {
      ["buyerAddress", "buyerName", "buyerTeId", "buyerScNumber", "buyerState", "buyerCountry", "buyerPostalCode"].forEach(
        (field) => formRef.current?.setFieldValue(field, "")
      );

      setDisable((prev) => ({
        ...prev,
        buyerAddress: false,
        buyerScNumber: false,
        buyerState: false,
        buyerCountry: false,
        buyerPostalCode: false,
      }));
      setVisible((prev) => ({ ...prev, buyerTeIdLabel: true }));
    } else {
      formRef.current?.setFieldValue("buyerName", value.account_name);
      formRef.current?.setFieldValue("buyerTeId", value.TE_ID);

      // Address
      if (value.street) {
        formRef.current?.setFieldValue("buyerAddress", value.street);
        setDisable((prev) => ({ ...prev, buyerAddress: true }));
      } else {
        formRef.current?.setFieldValue("buyerAddress", "");
        setDisable((prev) => ({ ...prev, buyerAddress: false }));
      }

      // SC Number
      if (value.sc_number) {
        formRef.current?.setFieldValue(
          "buyerScNumber",
          value.sc_number
        );
        setDisable((prev) => ({ ...prev, buyerScNumber: true }));
      } else {
        formRef.current?.setFieldValue("buyerScNumber", "");
        setDisable((prev) => ({ ...prev, buyerScNumber: false }));
      }

      // State
      if (value.state_name) {
        formRef.current?.setFieldValue("buyerState", value.state_name);
        setDisable((prev) => ({ ...prev, buyerState: true }));
      } else {
        formRef.current?.setFieldValue("buyerState", "");
        setDisable((prev) => ({ ...prev, buyerState: false }));
      }

      // Country
      if (value.country_name) {
        formRef.current?.setFieldValue("buyerCountry", value.country_name);
        setDisable((prev) => ({ ...prev, buyerCountry: true }));
      } else {
        formRef.current?.setFieldValue("buyerCountry", "");
        setDisable((prev) => ({ ...prev, buyerCountry: false }));
      }

      // Postal Code
      if (value.postal_code) {
        formRef.current?.setFieldValue("buyerPostalCode", value.postal_code);
        setDisable((prev) => ({ ...prev, buyerPostalCode: true }));
      } else {
        formRef.current?.setFieldValue("buyerPostalCode", "");
        setDisable((prev) => ({ ...prev, buyerPostalCode: false }));
      }

      setVisible((prev) => ({ ...prev, buyerTeIdLabel: false }));
    }
  };

  // ------------------ Form Config ------------------
  const formConfig = {
    pageName: "buyer-seller-details",
    initialValues: { ...detailsData, sellerHasTeid: "Yes" },
    validationSchema,
    enableReinitialize: true,
    sections: [
      {
        title: "Seller Details",
        fields: [
          {
            label: "Does Seller have a TE-ID?",
            name: "sellerHasTeid",
            type: "radio",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: sellerHasTeid,
            props: {
              options: [
                { label: "Yes", value: "Yes" },
                { label: "No", value: "No" },
              ],
              onChange: (e: any) => {
                const v = e.target.value as "Yes" | "No";
                setSellerHasTeid(v);
                // keep existing form values; no reinitialize
                if (formRef.current?.setFieldValue) {
                  formRef.current.setFieldValue("sellerHasTeid", v);
                }
              },
            },
          },
          // Seller TE ID (Search when Yes)
          {
            label: "Seller TE-ID",
            name: "sellerTeId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: detailsData.sellerTeId,
            visible: () => sellerHasTeid === "Yes",
            props: {
              placeholder: "Search Seller TE-ID",
              variant: "outlined",
              restrictInputFormat: true,
              param: "SellerTEId",
              onChange: handleSellerTEIdValueChange,
              displayKey: "TE_ID",
              // Show recently selected TE IDs when the field is focused and empty
              fetchOnEmptyFocus: true,
              onSearch: (query: string, offset: number) =>
                !query || query.trim() === ""
                  ? Promise.resolve(
                      recentTEIds.length > 0 ? recentTEIds : []
                    )
                  : fetchTEDetailsFromAPI(query, offset, "query"),
              fullWidth: true,
            },
          },
          // Seller TE-ID (free text when No)
          {
            label: "Seller TE-ID",
            name: "sellerTeId",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            disabled: true,
            visible: () => sellerHasTeid === "No",
            props: {
              placeholder: "Enter Seller TE-ID",
              variant: "outlined",
              inputProps: { maxLength: 11 },
              fullWidth: true,
            },
          },
          // Seller Name (search when Yes)
          {
            label: "Seller Name",
            name: "sellerName",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerName,
            visible: () => sellerHasTeid === "Yes" && visible.sellerName,
            props: {
              placeholder: "Search Seller Name",
              variant: "outlined",
              onChange: handleSellerNamedValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "name"),
              fullWidth: true,
            },
          },
          // Seller Name (read-only after lookup, for Yes flow)
          {
            label: "Seller Name",
            name: "sellerName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            disabled: true,
            visible: () => sellerHasTeid === "Yes" && !visible.sellerName,
            props: {
              placeholder: "Enter Seller Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Seller Name (editable text when No)
          {
            label: "Seller Name",
            name: "sellerName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            disabled: false,
            visible: () => sellerHasTeid === "No",
            props: {
              placeholder: "Enter Seller Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Seller Address (read-only when Yes + lookup, editable when No)
          {
            label: "Seller Address",
            name: "sellerAddress",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerAddress,
            // 🔑 key change: address is only disabled in the “Yes + auto-fill” flow
            disabled: sellerHasTeid === "Yes" && disable.sellerAddress,
            props: {
              placeholder: "Enter Seller Address",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Seller State
          {
            label: "Seller State",
            name: "sellerState",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerState,
            disabled: sellerHasTeid === "Yes" && disable.sellerState,
            props: {
              placeholder: "Enter Seller State",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Seller Country
          {
            label: "Seller Country",
            name: "sellerCountry",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerCountry,
            disabled: sellerHasTeid === "Yes" && disable.sellerCountry,
            props: {
              placeholder: "Enter Seller Country",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Seller Postal Code
          {
            label: "Seller Postal Code",
            name: "sellerPostalCode",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: detailsData.sellerPostalCode,
            disabled: sellerHasTeid === "Yes" && disable.sellerPostalCode,
            props: {
              placeholder: "Enter Seller Postal Code",
              variant: "outlined",
              fullWidth: true,
            },
          },
        ],
      },
      {
        title: "Buyer Details",
        fields: [
          // Buyer TE ID (Search with clear)
          {
            label: "Buyer TE-ID",
            name: "buyerTeId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerTeId,
            visible: () => true,
            props: {
              placeholder: "Search Buyer TE-ID",
              variant: "outlined",
              restrictInputFormat: true,
              onChange: handleBuyerTEIdValueChange,
              displayKey: "TE_ID",
              // Show recently selected TE IDs when the field is focused and empty
              fetchOnEmptyFocus: true,
              onSearch: (query: string, offset: number) =>
                !query || query.trim() === ""
                  ? Promise.resolve(
                      recentTEIds.length > 0 ? recentTEIds : []
                    )
                  : fetchTEDetailsFromAPI(query, offset, "query"),
              fullWidth: true,
            },
          },
          // Buyer Name (Search / Text)
          {
            label: "Buyer Name",
            name: "buyerName",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerName,
            visible: () => visible.buyerName,
            props: {
              placeholder: "Search Buyer Name",
              variant: "outlined",
              onChange: handleBuyerNameValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "name"),
              fullWidth: true,
            },
          },
          {
            label: "Buyer Name",
            name: "buyerName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            disabled: true,
            visible: () => !visible.buyerName,
            props: {
              placeholder: "Enter Buyer Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Address + SC Number
          {
            label: "Buyer Address",
            name: "buyerAddress",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerAddress,
            disabled: disable.buyerAddress,
            props: {
              placeholder: "Enter Buyer Address",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Buyer SC Number",
            name: "buyerScNumber",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerScNumber,
            disabled: false,
            props: {
              placeholder: "Enter SC Number",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Buyer State
          {
            label: "Buyer State",
            name: "buyerState",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerState,
            disabled: disable.buyerState,
            props: {
              placeholder: "Enter Buyer State",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Buyer Country
          {
            label: "Buyer Country",
            name: "buyerCountry",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerCountry,
            disabled: disable.buyerCountry,
            props: {
              placeholder: "Enter Buyer Country",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Buyer Postal Code
          {
            label: "Buyer Postal Code",
            name: "buyerPostalCode",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: detailsData.buyerPostalCode,
            disabled: disable.buyerPostalCode,
            props: {
              placeholder: "Enter Buyer Postal Code",
              variant: "outlined",
              fullWidth: true,
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => {setDetailsData(values);
      
      const errors = (await formRef.current?.validateForm()) ?? {};
      if (Object.keys(errors || {}).length > 0) {
        notify("Please fill all the required fields", "error");
        return;
      }
    },
  };

  // ------------------ Render ------------------
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 6, fontWeight: "bold" }}>
        Buyer & Seller Details
      </Typography>

      <FormBuilder
        formConfig={formConfig}
        onSubmit={formConfig.handleSubmit}
        formRef={formRef}
      />
    </Box>
  );
};

export default BuyerSellerDetailsForm;
